import Image from '@tiptap/extension-image';
import { Table, TableCell, TableHeader, TableRow } from '@tiptap/extension-table';
import StarterKit from '@tiptap/starter-kit';

/**
 * The ONE schema the component recognises.
 *
 * Both the editor and the read-only display mount this exact set, which is what
 * makes the display a sanitiser: stored HTML is re-parsed into a ProseMirror
 * document, so a node or mark that is not in this list cannot survive the round
 * trip. There is no scrubbing regex and no `dangerouslySetInnerHTML` anywhere.
 *
 * StarterKit v3 already carries the marks and nodes the toolbar needs — bold,
 * italic, strike, code, underline, link, blockquote, code block, headings,
 * lists, horizontal rule and undo/redo — so only the table and image nodes are
 * added here.
 */
export function createExtensions() {
  return [
    StarterKit.configure({
      heading: { levels: [2, 3] },
      // A link is an editing affordance, not a navigation: clicking one inside
      // the surface must place the cursor, never leave the page mid-edit.
      link: {
        openOnClick: false,
        autolink: true,
        defaultProtocol: 'https',
      },
    }),
    Table.configure({ resizable: false }),
    TableRow,
    TableHeader,
    TableCell,
    Image.configure({ inline: false, allowBase64: false }),
  ];
}

/**
 * The nodes that are content even though they carry no text.
 *
 * The closed schema has exactly two: an image and a horizontal rule.
 */
const NON_TEXT_CONTENT = /<(?:img|hr)\b/i;

/** Entities that render as whitespace, so they are not content either. */
const BLANK_ENTITIES = /&(?:nbsp|#160|#xa0);/gi;

/**
 * Tiptap's empty document serialises as `<p></p>`; the value contract says an
 * empty document is `''` (hosts treat `null` and `''` identically).
 *
 * A document can hold structure and still be contentless — an empty heading, a
 * bare blockquote, a paragraph with only a `<br>`. Those are empty too: a
 * required field must not be satisfied by markup with nothing in it. `isEmpty`
 * is Tiptap's own answer for a live document (`editor.isEmpty`); the string
 * rule covers values that arrive before anything is mounted.
 */
export function normaliseHtml(html: string, isEmpty = false): string {
  if (isEmpty || html === '') return '';
  const text = html
    .replace(/<[^>]*>/g, '')
    .replace(BLANK_ENTITIES, '')
    .trim();
  return text === '' && !NON_TEXT_CONTENT.test(html) ? '' : html;
}
